import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { IFeature } from "src/pages/AdminCenter/models/interfaces";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";

/**
 * App-license-expiry is a tenant-level lock driven by a single `dim_features` record that the
 * backend already surfaces in the metadata response (`activeFeatures`). The FE reads it read-only.
 *
 * The config lives in `version_list[0].description` as a pipe-delimited descriptor, e.g.
 *   `t:"2026-06-11T07:15:40.529000"|"Your access will expire in {t}. Please contact your administrator"|n:"7"`
 *   - `t` — the expiry datetime (UTC).
 *   - the bare quoted segment — the BE-authored message template; its `{t}` token is replaced on the
 *     FE with a live remaining-time phrase (days → hours → minutes).
 *   - `n` — how many days before expiry the countdown banner should start showing.
 *
 * The banner counts down only *before* expiry; once `now >= t` it hides (the backend's 403 hard
 * cutoff / full-page splash owns the post-expiry experience). It is always shown in the theme red.
 * The live countdown refreshes every 30s while visible.
 */

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const ONE_HOUR_MS = 60 * 60 * 1000;
export const ONE_MINUTE_MS = 60 * 1000;

// Lead window used when the descriptor omits `n`, and the day thresholds at which colour escalates.
export const DEFAULT_LEAD_DAYS = 30;
export const WARNING_TIER_DAYS = 14; // <= 14 days remaining → "warning" (or worse)
export const CRITICAL_TIER_DAYS = 7; // <= 7 days remaining → "critical"

// How often the visible countdown refreshes so the displayed remaining time stays current.
export const COUNTDOWN_REFRESH_MS = 30 * 1000;

// FE fallback template (i18n key) used only when the backend omits a message template.
export const APP_LICENSE_EXPIRY_FALLBACK_TEMPLATE = "Your access will expire in {t}. Please contact your administrator.";

export type AppLicenseStatus = "normal" | "expiring" | "expired";
export type AppLicenseSeverity = "info" | "warning" | "critical";

export interface AppLicenseState {
	status: AppLicenseStatus;
	expirationDate: string | null;
	// BE-authored message template containing a `{t}` token for the dynamic remaining-time. Null → FE fallback.
	messageTemplate: string | null;
	// Milliseconds until expiry; positive while the banner is shown ("expiring").
	msRemaining: number;
	// Colour tier for the banner + avatar badge; null when status is not "expiring".
	severity: AppLicenseSeverity | null;
}

const NORMAL: AppLicenseState = { status: "normal", expirationDate: null, messageTemplate: null, msRemaining: NaN, severity: null };

/**
 * Single colour for the banner, the navbar avatar badge, and the popover message — the theme's red
 * (`--warning-red`), so every whitelabel stays on-brand. The banner is always shown in red regardless
 * of how close expiry is; `severity` is still computed (below) but no longer drives the palette/icon.
 */
export const APP_LICENSE_BANNER_COLOR = "var(--warning-red)";

/** Severity tier from the time remaining. */
const severityFor = (msRemaining: number): AppLicenseSeverity => {
	if (msRemaining <= CRITICAL_TIER_DAYS * ONE_DAY_MS) return "critical";
	if (msRemaining <= WARNING_TIER_DAYS * ONE_DAY_MS) return "warning";
	return "info";
};

/**
 * Parse a backend datetime to an epoch (ms). Backend datetimes are UTC but may omit the trailing
 * `Z` and carry microsecond precision (6 fractional digits). We normalise both so comparisons are
 * timezone-independent and parse reliably across engines:
 *   - truncate fractional seconds to milliseconds (3 digits)
 *   - append `Z` when there is no explicit timezone designator (else it would parse as local time)
 * Returns `NaN` for anything that is not a parseable datetime.
 */
const toUtcMs = (value: string): number => {
	const trimmed = value.trim();
	const msNormalized = trimmed.replace(/(\.\d{3})\d+/, "$1");
	const hasTz = /[zZ]$/.test(msNormalized) || /[+-]\d{2}:?\d{2}$/.test(msNormalized);
	return Date.parse(hasTz ? msNormalized : `${msNormalized}Z`);
};

/** Strip surrounding double-quotes and whitespace from a descriptor segment. */
const unquote = (value: string): string => value.trim().replace(/^"([\s\S]*)"$/, "$1").trim();

export interface LicenseDescriptor {
	expiryRaw: string | null;
	messageTemplate: string | null;
	leadDays: number | null;
}

/**
 * Parse the pipe-delimited descriptor. Segments are matched by prefix (`t:` → datetime, `n:` →
 * lead-days) so their order is irrelevant; the remaining bare quoted segment is the message
 * template. Tolerates a plain ISO date with no prefixes (treated as the datetime).
 */
export const parseLicenseDescriptor = (description?: string | null): LicenseDescriptor => {
	const result: LicenseDescriptor = { expiryRaw: null, messageTemplate: null, leadDays: null };
	if (!description) return result;

	description.split("|").forEach((segRaw) => {
		const seg = segRaw.trim();
		if (!seg) return;
		if (/^t\s*:/i.test(seg)) {
			result.expiryRaw = unquote(seg.replace(/^t\s*:/i, ""));
		} else if (/^n\s*:/i.test(seg)) {
			const n = parseInt(unquote(seg.replace(/^n\s*:/i, "")), 10);
			if (!Number.isNaN(n)) result.leadDays = n;
		} else if (result.messageTemplate === null && seg.includes("{t}")) {
			result.messageTemplate = unquote(seg);
		} else if (result.expiryRaw === null && !Number.isNaN(toUtcMs(unquote(seg)))) {
			// Bare ISO datetime with no `t:` prefix (back-compat with the original wire shape).
			result.expiryRaw = unquote(seg);
		}
	});
	return result;
};

/** Humanised remaining-time phrase (days → hours → minutes) for the `{t}` token. */
export const formatRemaining = (msRemaining: number): { key: string; vars?: Record<string, unknown> } => {
	const days = Math.floor(msRemaining / ONE_DAY_MS);
	if (days >= 1) return days === 1 ? { key: "1 day" } : { key: "{{value}} days", vars: { value: days } };

	const hours = Math.floor(msRemaining / ONE_HOUR_MS);
	if (hours >= 1) return hours === 1 ? { key: "1 hour" } : { key: "{{value}} hours", vars: { value: hours } };

	const minutes = Math.max(1, Math.ceil(msRemaining / ONE_MINUTE_MS));
	return minutes === 1 ? { key: "1 minute" } : { key: "{{value}} minutes", vars: { value: minutes } };
};

/**
 * Compose the final banner / popover text by substituting the `{t}` token in the (BE-authored or
 * fallback) template with the already-localized remaining-time phrase. Pure — no `t` dependency.
 */
export const composeAppLicenseMessage = (template: string | null, durationText: string, fallbackTemplate: string): string =>
	(template || fallbackTemplate).replace(/\{t\}/g, durationText);

/**
 * Stable identity for a single license-warning instance — the (expiry datetime, message template)
 * pair. The banner is dismissed against this key (not the expiry date alone), so a backend push that
 * changes *either* the expiry time *or* the message text yields a new key and resurfaces a banner the
 * user had previously dismissed. Only used for equality comparison, so the exact encoding is opaque.
 */
export const appLicenseDismissKey = (expirationDate: string | null, messageTemplate: string | null): string =>
	JSON.stringify([expirationDate, messageTemplate]);

/**
 * Pure resolver — kept separate from the hook so it is trivially unit-testable.
 */
export const computeAppLicenseStatus = (features: IFeature[] | undefined, now: number = Date.now()): AppLicenseState => {
	const feature = features?.find((f) => f.feature_key === FeatureKeysType.AppLicenseExpiry);
	// Feature absent or explicitly disabled → FE does nothing.
	if (!feature || feature.isEnabled === false) return NORMAL;

	const v1 = feature.version_list?.[0];
	if (!v1 || v1.enabled === false) return NORMAL;

	const { expiryRaw, leadDays, messageTemplate } = parseLicenseDescriptor(v1.description);
	const rawExpiry = expiryRaw || v1.expiration_date || null;
	if (!rawExpiry) return NORMAL;

	const expiresAt = toUtcMs(rawExpiry);
	if (Number.isNaN(expiresAt)) return NORMAL;

	const leadWindowMs = (leadDays && leadDays > 0 ? leadDays : DEFAULT_LEAD_DAYS) * ONE_DAY_MS;

	// Once expiry passes, the FE banner hides — the backend 403 / full-page splash takes over.
	if (now >= expiresAt) return { status: "expired", expirationDate: rawExpiry, messageTemplate, msRemaining: NaN, severity: null };

	// Banner window: from `n` (or 30) days before expiry up to expiry. Outside it → nothing.
	if (now < expiresAt - leadWindowMs) return { status: "normal", expirationDate: rawExpiry, messageTemplate, msRemaining: NaN, severity: null };

	const msRemaining = expiresAt - now;
	return { status: "expiring", expirationDate: rawExpiry, messageTemplate, msRemaining, severity: severityFor(msRemaining) };
};

export const useAppLicenseStatus = (): AppLicenseState & { isDismissed: boolean; dismissKey: string | null } => {
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);
	// Persisted (redux-persist) dismissal key the user dismissed the banner for. The banner is hidden
	// only while it matches the live (expiry + message) key, so a renewed license OR a changed message
	// resurfaces the banner (see appLicenseDismissKey).
	const dismissedFor = useSelector((state: RootState) => state.studiesSlice.appLicenseBannerDismissedFor);
	const [now, setNow] = useState(() => Date.now());

	const computed = useMemo(() => computeAppLicenseStatus(activeFeatures, now), [activeFeatures, now]);

	// Live countdown: refresh `now` every 30s while the banner is active so the displayed remaining
	// time stays current without a reload. Only runs during the "expiring" window — no idle timers.
	useEffect(() => {
		if (computed.status !== "expiring") return undefined;
		const id = setInterval(() => setNow(Date.now()), COUNTDOWN_REFRESH_MS);
		return () => clearInterval(id);
	}, [computed.status]);

	// Identity of the current warning instance; what the banner records on dismiss. A backend change
	// to either the expiry time or the message template produces a different key, so the dismissal no
	// longer matches and the banner (and avatar/popover indicator, which keys off `isDismissed`) returns.
	const dismissKey = computed.expirationDate ? appLicenseDismissKey(computed.expirationDate, computed.messageTemplate) : null;

	const isDismissed = computed.status === "expiring" && !!dismissKey && dismissedFor === dismissKey;
	return { ...computed, isDismissed, dismissKey };
};
